/* Optional, priced protection. A customer's removal persists on the cart. */
(() => {
  'use strict';
  const node = document.getElementById('nytc-protection-config');
  if (!node || window.nytcPackageProtection) return;
  const config = JSON.parse(node.textContent);
  const root = window.Shopify?.routes?.root || '/';
  const attribute = '_nytc_package_protection';
  let running = null;
  let timer;
  let removing = false;
  let queued = false;
  let declinedToken = null;
  const lines = cart => cart.items.filter(item => item.product_id === config.productId);
  const storageKey = cart => 'nytc:protection:declined:' + cart.token;
  function declined(cart) {
    if (cart.attributes?.[attribute] === 'declined' || declinedToken === cart.token) return true;
    try { return sessionStorage.getItem(storageKey(cart)) === '1'; } catch (_) { return false; }
  }
  function status(message) {
    document.querySelectorAll('[data-nytc-protection-status]').forEach(el => { el.textContent = message; });
  }
  async function request(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(root + path, {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        ...(body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {})
      });
      if (!response.ok) throw new Error('Cart request failed');
      return await response.json();
    } finally { clearTimeout(timeout); }
  }
  function refresh(cart) {
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  }
  function chooseTier(cart) {
    const merchandise = cart.items.filter(item => item.product_id !== config.productId);
    if (!merchandise.some(item => item.requires_shipping && item.quantity > 0)) return null;
    // Use merchandise after line discounts, before cart discounts, excluding protection.
    const subtotal = merchandise.reduce((sum, item) => sum + item.final_line_price, 0);
    if (!subtotal) return null;
    let rate = 1;
    if (cart.currency !== config.currency) {
      const currency = window.Shopify?.currency;
      rate = Number(currency?.rate);
      // Never guess a fee when the storefront has no matching conversion rate.
      if (currency?.active !== cart.currency || !Number.isFinite(rate) || rate <= 0) return undefined;
    }
    const total = Math.round(subtotal / rate);
    const sku = total < 5000 ? 'PP-T1-50' : total < 15000 ? 'PP-T2-150' : total < 20000 ? 'PP-T3-200' : 'PP-T4-MAX';
    const tier = config.tiers[sku];
    return tier?.available ? tier.id : undefined;
  }
  async function reconcile() {
    const cart = await request('cart.js');
    const existing = lines(cart);
    const optedOut = declined(cart);
    const desired = optedOut ? null : chooseTier(cart);
    const persistDecline = optedOut && cart.attributes?.[attribute] !== 'declined';
    if (desired === undefined) return;
    if (!persistDecline && existing.length === (desired ? 1 : 0) && (!desired || (existing[0].variant_id === desired && existing[0].quantity === 1))) return;
    const updates = {};
    existing.forEach(item => { updates[item.key] = 0; });
    if (desired) {
      const match = existing.find(item => item.variant_id === desired);
      updates[match ? match.key : desired] = 1;
    }
    refresh(await request('cart/update.js', { updates, ...(persistDecline ? { attributes: { [attribute]: 'declined' } } : {}) }));
  }
  function sync() {
    if (running || removing) { queued = true; return running; }
    running = reconcile().catch(() => { /* Optional service failure must not block shopping. */ }).finally(() => {
      running = null;
      if (queued) { queued = false; schedule(); }
    });
    return running;
  }
  function schedule() { clearTimeout(timer); timer = setTimeout(sync, 180); }
  async function remove() {
    if (removing) return;
    removing = true;
    clearTimeout(timer);
    const buttons = [...document.querySelectorAll('[data-nytc-protection-remove]')];
    buttons.forEach(button => { button.disabled = true; });
    status(config.updating);
    try {
      if (running) await running;
      const cart = await request('cart.js');
      declinedToken = cart.token;
      try { sessionStorage.setItem(storageKey(cart), '1'); } catch (_) { /* Cart attribute is the durable preference. */ }
      const updates = {};
      lines(cart).forEach(item => { updates[item.key] = 0; });
      const updated = await request('cart/update.js', { updates, attributes: { [attribute]: 'declined' } });
      refresh(updated);
      status(config.removed);
    } catch (_) {
      status(config.removeError);
    } finally {
      removing = false;
      buttons.forEach(button => { button.disabled = false; });
      if (queued) { queued = false; schedule(); }
    }
  }
  document.addEventListener('click', event => {
    if (!event.target.closest('[data-nytc-protection-remove]')) return;
    event.preventDefault();
    remove();
  });
  // Keep the cart visible while a price/removal is being saved; never submit an unseen fee.
  document.addEventListener('submit', event => {
    if (!event.target.matches('#CartDrawerForm, #CartPageForm') || (!running && !removing)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    status(config.updating);
    Promise.resolve(running).finally(() => { if (!removing) status(''); });
  }, true);
  ['ajaxProduct:added', 'cart:updated', 'cart:build', 'cart:open', 'page:loaded'].forEach(name => document.addEventListener(name, schedule));
  window.addEventListener('pageshow', schedule);
  document.querySelectorAll('#CartDrawer [data-products], #CartPageForm [data-products]').forEach(el => {
    new MutationObserver(schedule).observe(el, { childList: true, subtree: true });
  });
  const drawer = document.getElementById('CartDrawer');
  if (drawer) new MutationObserver(schedule).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  window.nytcPackageProtection = { sync };
  sync();
})();
